import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n';
import PublicLayout from '../components/PublicLayout';
import { API } from '../auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export default function ContactsPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then((r) => setS(r.data))
      .catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const list = JSON.parse(localStorage.getItem('contact_messages') || '[]');
    list.push({ ...form, ts: new Date().toISOString() });
    localStorage.setItem('contact_messages', JSON.stringify(list));
    toast.success(t('sendMessage') + ' ✓');
    setForm({ name: '', email: '', message: '' });
  };

  const email = s?.email || s?.cs_email || '';
  const phone = s?.phone || s?.cs_phone || '';
  const address = s?.address_ko || '';
  const company = s?.company_name_ko || '';
  const bizReg = s?.business_reg_number || '';

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="contacts-page">
        <p className="eyebrow mb-6">{t('contactsTitle')}</p>
        <h1 className="h-display text-5xl md:text-7xl mb-16 max-w-3xl">
          {t('contactsH1Line1')} <span className="text-[#C9A352]">{t('contactsH1Line2')}</span>
        </h1>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Info */}
          <div className="space-y-10" data-testid="contacts-info">
            <div>
              <div className="text-[#556B2F] mb-3"><Mail className="h-5 w-5" /></div>
              <p className="eyebrow mb-2">Email</p>
              {email ? (
                <a href={`mailto:${email}`} className="text-xl font-semibold text-[#1A1A1A] hover:text-[#556B2F]" data-testid="contact-email-link">
                  {email}
                </a>
              ) : <p className="text-[#1A1A1A]/40">—</p>}
            </div>
            <div>
              <div className="text-[#556B2F] mb-3"><Phone className="h-5 w-5" /></div>
              <p className="eyebrow mb-2">Phone · 전화</p>
              {phone ? (
                <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="text-xl font-semibold text-[#1A1A1A] hover:text-[#556B2F]" data-testid="contact-phone-link">
                  {phone}
                </a>
              ) : <p className="text-[#1A1A1A]/40">—</p>}
              {s?.customer_service_hours && (
                <p className="text-sm text-[#1A1A1A]/50 mt-2">{s.customer_service_hours}</p>
              )}
            </div>
            <div>
              <div className="text-[#556B2F] mb-3"><MapPin className="h-5 w-5" /></div>
              <p className="eyebrow mb-2">Address · 주소</p>
              <p className="text-lg font-semibold text-[#1A1A1A] leading-snug" data-testid="contact-address">{address || '—'}</p>
              {company && <p className="text-sm text-[#1A1A1A]/60 mt-2">{company}</p>}
              {bizReg && <p className="text-sm text-[#1A1A1A]/50 mt-1">사업자등록번호 {bizReg}</p>}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="bg-[#F2EFE6] rounded-xl p-8 md:p-10 space-y-5" data-testid="contact-form">
            <h3 className="font-bold text-2xl mb-2">{t('contactsForm')}</h3>
            <div>
              <Label className="eyebrow">{t('contactName')}</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="mt-2 bg-[#F6F4EE] border-[#A4B18E]/40 focus:ring-[#556B2F]"
                data-testid="contact-name"
              />
            </div>
            <div>
              <Label className="eyebrow">Email</Label>
              <Input
                type="email" required
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                className="mt-2 bg-[#F6F4EE] border-[#A4B18E]/40 focus:ring-[#556B2F]"
                data-testid="contact-email"
              />
            </div>
            <div>
              <Label className="eyebrow">{t('contactMessage')}</Label>
              <Textarea
                required rows={5}
                value={form.message}
                onChange={(e) => setForm({...form, message: e.target.value})}
                className="mt-2 bg-[#F6F4EE] border-[#A4B18E]/40 focus:ring-[#556B2F]"
                data-testid="contact-message"
              />
            </div>
            <Button type="submit" className="cff-button-primary w-full h-12" data-testid="contact-submit">
              {t('sendMessage')} <Send className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
